import { useEffect, useState, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { api } from '../lib/api';
import { Skeleton } from '../components/Skeleton';
import { CostBreakdownCard } from '../components/dashboard/CostBreakdownCard';
import { RevenueCostChart } from '../components/dashboard/RevenueCostChart';
import { ProfitChart } from '../components/dashboard/ProfitChart';
import {
  buildCostLookup,
  buildRevenueLookup,
  buildUnitBreakdown,
  combinedChartRows,
  excludedCountFor,
  filterPeriod,
  monthsInRange,
  profitChartRows,
  propertiesForCurrency,
  type CostsMonthlyResponse,
  type RevenueMonthRow,
} from '../lib/dashboard-costs';
import { formatCurrency } from '../lib/format';

interface Property {
  id: number;
  name: string;
  location: string;
  currency: string;
  total_payout: number;
  booking_count: number;
  nights_booked: number;
}

interface Booking {
  id: number;
  guest_name: string | null;
  property_name: string;
  checkin_date: string;
  checkout_date: string;
  nights: number;
  gross_payout: number;
  currency: string;
}

interface BookingsSummary {
  by_property: Property[];
  by_month: RevenueMonthRow[];
}

function formatDate(d: string) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const LOCATION_COLORS: Record<string, string> = {
  switzerland: 'bg-emerald-500',
  dubai: 'bg-amber-500',
};

const PROPERTY_COLORS = ['#10b981', '#f59e0b', '#6366f1'];
const CURRENCY_PANE_ORDER = ['CHF', 'AED'];

const PROPERTY_VALUES: Record<string, { value: number; currency: string }> = {
  'Altendorf':          { value: 1130000, currency: 'CHF' },
  'Dubai JLT Goldcrest':{ value: 768000,  currency: 'AED' },
  'Dubai Majestine':    { value: 919000,  currency: 'AED' },
};
const TIME_FILTERS: { label: string; months: number | null }[] = [
  { label: '3M', months: 3 },
  { label: '6M', months: 6 },
  { label: '12M', months: 12 },
  { label: 'All', months: null },
];

export default function Dashboard() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [summary, setSummary] = useState<BookingsSummary>({ by_property: [], by_month: [] });
  const [costsMonthly, setCostsMonthly] = useState<CostsMonthlyResponse>({ by_month: [], excluded: [] });
  const [recentBookings, setRecentBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('All');
  const [includePrincipal, setIncludePrincipal] = useState(true);

  useEffect(() => {
    Promise.all([
      api.getProperties(),
      api.getBookingsSummary(),
      api.getBookings({}),
      api.getCostsMonthly(),
    ]).then(([props, sum, bookings, costs]) => {
      setProperties(props);
      setSummary(sum);
      setRecentBookings((bookings as Booking[]).slice(0, 10));
      setCostsMonthly(costs);
      setLoading(false);
    }).catch((err) => {
      console.error('Failed to load dashboard:', err);
      setLoading(false);
    });
  }, []);

  const tf = TIME_FILTERS.find(f => f.label === timeFilter) ?? TIME_FILTERS[TIME_FILTERS.length - 1];
  const { cutoff, prevCutoff } = filterPeriod(tf.months);

  // One shared revenue derivation for the revenue chart AND the profit chart.
  const revenueLookup = useMemo(
    () => buildRevenueLookup(summary.by_month),
    [summary.by_month],
  );
  // All-categories cost lookup feeds the revenue-chart overlay; the profit
  // chart swaps to the principal-free variant when the toggle is off.
  const costLookupAll = useMemo(
    () => buildCostLookup(costsMonthly.by_month),
    [costsMonthly.by_month],
  );
  const costLookupNoPrincipal = useMemo(
    () => buildCostLookup(costsMonthly.by_month, { includePrincipal: false }),
    [costsMonthly.by_month],
  );
  const costLookupForProfit = includePrincipal ? costLookupAll : costLookupNoPrincipal;

  const propertyColors = useMemo(
    () => Object.fromEntries(properties.map((p, i) => [p.name, PROPERTY_COLORS[i % PROPERTY_COLORS.length]])),
    [properties],
  );

  const paneCurrencies = CURRENCY_PANE_ORDER.filter(
    (c) => properties.some((p) => p.currency === c),
  );

  // Sorted union of revenue + cost months for one currency pane, in range.
  const monthsForPane = (currency: string, costLookup: typeof costLookupAll): string[] => {
    const monthSet = new Set<string>();
    for (const m of revenueLookup.get(currency)?.keys() ?? []) monthSet.add(m);
    for (const m of costLookup.get(currency)?.keys() ?? []) monthSet.add(m);
    return monthsInRange(monthSet, cutoff);
  };

  // Yield chart data — annualised monthly yield per property (in own currency, no conversion needed)
  const yieldData = useMemo(() => {
    const monthSet = new Set(
      summary.by_month
        .filter(r => !cutoff || r.month >= cutoff)
        .map(r => r.month)
    );

    return Array.from(monthSet).sort().map(m => {
      const row: Record<string, string | number> = { month: m };
      for (const r of summary.by_month) {
        if (r.month !== m) continue;
        const propVal = PROPERTY_VALUES[r.property_name];
        if (!propVal) continue;
        // Annualised yield: (monthly payout / property value) × 12 × 100
        const yield_pct = ((r.total_payout / propVal.value) * 12 * 100);
        row[r.property_name] = parseFloat(yield_pct.toFixed(2));
      }
      return row;
    });
  }, [summary.by_month, cutoff]);

  if (loading) {
    return (
      <div className="space-y-8">
        <Skeleton className="h-8 w-64" />

        {/* Property cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow border border-gray-200 p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Skeleton className="w-3 h-3 rounded-full" />
                <Skeleton className="h-5 w-40" />
              </div>
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-8 w-32" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
              </div>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6 space-y-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-80 w-full rounded-lg" />
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6 space-y-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-72 w-full rounded-lg" />
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6 space-y-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-72 w-full rounded-lg" />
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <Skeleton className="h-6 w-40" />
          </div>
          <div className="divide-y divide-gray-100">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="px-6 py-3 flex gap-4">
                <Skeleton className="h-4 w-1/6" />
                <Skeleton className="h-4 w-1/6" />
                <Skeleton className="h-4 w-1/6" />
                <Skeleton className="h-4 w-1/6" />
                <Skeleton className="h-4 w-1/6" />
                <Skeleton className="h-4 w-1/6" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Group monthly revenue by currency (for table)
  const monthlyByCurrency: Record<string, Record<string, number>> = {};
  for (const row of summary.by_month) {
    if (!monthlyByCurrency[row.month]) monthlyByCurrency[row.month] = {};
    monthlyByCurrency[row.month][row.currency] = (monthlyByCurrency[row.month][row.currency] || 0) + row.total_payout;
  }
  const months = Object.keys(monthlyByCurrency).sort().reverse();

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-gray-900">Financial Overview</h1>

      {/* Property Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {properties.map((p) => (
          <div key={p.id} className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className={`w-3 h-3 rounded-full ${LOCATION_COLORS[p.location] || 'bg-gray-400'}`} />
              <h2 className="text-lg font-semibold text-gray-900">{p.name}</h2>
            </div>
            <div className="text-xs text-gray-500 uppercase mb-3">{p.location}</div>
            <div className="space-y-3">
              <div className="flex justify-between items-baseline">
                <span className="text-sm text-gray-500">Total Payout</span>
                <span className="text-xl font-bold text-gray-900">
                  {formatCurrency(p.total_payout, p.currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Bookings</span>
                <span className="text-sm font-semibold text-gray-700">{p.booking_count}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Nights Booked</span>
                <span className="text-sm font-semibold text-gray-700">{p.nights_booked}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Per-unit cost breakdown */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Costs by Unit</h2>
            <p className="text-xs text-gray-400 mt-1">
              Rolled up by category prefix, expandable to raw categories — native amounts, no FX conversion
            </p>
          </div>
          <div className="flex gap-1">
            {TIME_FILTERS.map(f => (
              <button
                key={f.label}
                onClick={() => setTimeFilter(f.label)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  timeFilter === f.label
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {properties.map((p) => {
            const currentRows = costsMonthly.by_month.filter(
              (r) => r.property_id === p.id && (!cutoff || r.month >= cutoff),
            );
            const prevRows = prevCutoff && cutoff
              ? costsMonthly.by_month.filter(
                  (r) => r.property_id === p.id && r.month >= prevCutoff && r.month < cutoff,
                )
              : [];
            return (
              <CostBreakdownCard
                key={p.id}
                propertyName={p.name}
                currency={p.currency}
                breakdown={buildUnitBreakdown(currentRows, prevRows)}
                excludedCount={excludedCountFor(costsMonthly.excluded, p.id)}
              />
            );
          })}
        </div>
      </div>

      {/* Revenue Over Time Chart — one pane per currency, native amounts */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Revenue Over Time</h2>
            <p className="text-xs text-gray-400 mt-1">
              Native amounts per currency — no FX conversion; dashed lines are costs
            </p>
          </div>
          <div className="flex gap-1">
            {TIME_FILTERS.map(f => (
              <button
                key={f.label}
                onClick={() => setTimeFilter(f.label)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  timeFilter === f.label
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-6 space-y-8">
          {paneCurrencies.map((currency) => {
            const paneProperties = propertiesForCurrency(revenueLookup, costLookupAll, currency);
            const months = monthsForPane(currency, costLookupAll);
            return (
              <div key={currency}>
                <h3 className="text-sm font-semibold text-gray-500 mb-2">{currency}</h3>
                <RevenueCostChart
                  currency={currency}
                  rows={combinedChartRows(revenueLookup, costLookupAll, currency, months)}
                  properties={paneProperties}
                  colors={propertyColors}
                  height={280}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Profit Over Time Chart — one pane per currency */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Profit Over Time</h2>
            <p className="text-xs text-gray-400 mt-1">
              Revenue − costs per month, per property, native currency
            </p>
          </div>
          <div className="flex gap-1">
            {TIME_FILTERS.map(f => (
              <button
                key={f.label}
                onClick={() => setTimeFilter(f.label)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  timeFilter === f.label
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-6 space-y-8">
          {paneCurrencies.map((currency) => {
            const paneProperties = propertiesForCurrency(revenueLookup, costLookupForProfit, currency);
            const months = monthsForPane(currency, costLookupForProfit);
            return (
              <div key={currency}>
                <h3 className="text-sm font-semibold text-gray-500 mb-2">{currency}</h3>
                <ProfitChart
                  currency={currency}
                  rows={profitChartRows(revenueLookup, costLookupForProfit, currency, months)}
                  properties={paneProperties}
                  colors={propertyColors}
                  includePrincipal={includePrincipal}
                  onIncludePrincipalChange={setIncludePrincipal}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Yield Chart */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Annualised Yield</h2>
            <p className="text-xs text-gray-400 mt-1">
              Monthly payout ÷ property value × 12 — each property in its own currency&nbsp;
              (Altendorf CHF 1.13M · JLT AED 768k · Majestine AED 919k)
            </p>
          </div>
          <div className="flex gap-1">
            {TIME_FILTERS.map(f => (
              <button
                key={f.label}
                onClick={() => setTimeFilter(f.label)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  timeFilter === f.label
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-6">
          <ResponsiveContainerWrapper yieldData={yieldData} propertyColors={propertyColors} properties={properties} />
        </div>
      </div>

      {/* Monthly Revenue Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Monthly Revenue</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">CHF</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">AED</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {months.map((month) => (
                <tr key={month} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">{month}</td>
                  <td className="px-6 py-3 text-sm text-right text-gray-700">
                    {monthlyByCurrency[month].CHF
                      ? formatCurrency(monthlyByCurrency[month].CHF, 'CHF')
                      : <span className="text-gray-300">-</span>}
                  </td>
                  <td className="px-6 py-3 text-sm text-right text-gray-700">
                    {monthlyByCurrency[month].AED
                      ? formatCurrency(monthlyByCurrency[month].AED, 'AED')
                      : <span className="text-gray-300">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Bookings */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Bookings</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Guest</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Property</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-in</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-out</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Nights</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Payout</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {recentBookings.map((b) => (
                <tr key={b.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm text-gray-900">{b.guest_name || '-'}</td>
                  <td className="px-6 py-3 text-sm text-gray-700">{b.property_name}</td>
                  <td className="px-6 py-3 text-sm text-gray-700">{formatDate(b.checkin_date)}</td>
                  <td className="px-6 py-3 text-sm text-gray-700">{formatDate(b.checkout_date)}</td>
                  <td className="px-6 py-3 text-sm text-right text-gray-700">{b.nights}</td>
                  <td className="px-6 py-3 text-sm text-right font-medium text-gray-900">
                    {formatCurrency(b.gross_payout, b.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** Yield chart body, kept as a small inner component so Dashboard's early loading return stays readable. */
function ResponsiveContainerWrapper({
  yieldData,
  propertyColors,
  properties,
}: {
  yieldData: Record<string, string | number>[];
  propertyColors: Record<string, string>;
  properties: Property[];
}) {
  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={yieldData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} width={50} />
        <Tooltip formatter={(value, name) => [`${value}% p.a.`, name]} />
        <Legend />
        {properties.map((p) => (
          <Line key={p.name} type="monotone" dataKey={p.name}
            stroke={propertyColors[p.name] ?? '#94a3b8'}
            strokeWidth={2} dot={{ r: 3 }} connectNulls activeDot={{ r: 5 }} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
